import { Link } from "react-router-dom";

import PublicShell from "@/components/PublicShell";
import { site } from "@/lib/api";
import { useAsync } from "@/lib/hooks";
import { useSession } from "@/lib/session";

/**
 * What a stranger sees. It says what the app does and what it refuses to do,
 * because the second half is the reason to trust the first: the numbers on
 * screen are chemistry, and the assistant is never allowed near them.
 */
export default function Landing() {
  const info = useAsync(() => site.info().catch(() => null), []);
  const { status } = useSession();
  const contact = info.data?.contact_email;
  const canSignUp = info.data?.signup_mode === "open";

  return (
    <PublicShell contactEmail={contact}>
      <section className="pt-6 pb-10">
        <p className="unit">A logbook for your pool or hot tub</p>
        <h1 className="mt-3 max-w-xl text-4xl font-semibold leading-tight tracking-tight">
          Test the water. Type four numbers. Be told what to add.
        </h1>
        <p className="mt-4 max-w-xl text-lg text-(--color-ink-muted)">
          Pool Nerd keeps every reading, dose and event you log, works out target ranges from
          your own water, and sizes each dose for your volume from published pool chemistry —
          not from a chatbot.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {status.authenticated ? (
            <Link to="/" className="btn btn-primary">
              Open your pool
            </Link>
          ) : (
            <Link to="/login" className="btn btn-primary">
              {canSignUp ? "Sign in or create an account" : "Sign in"}
            </Link>
          )}
          <Link to="/guide" className="btn btn-quiet">
            Read the guide
          </Link>
          {info.data && !canSignUp && !status.authenticated && (
            <p className="text-sm text-(--color-ink-muted)">
              New accounts are by invitation for now.
              {contact && (
                <>
                  {" "}
                  Ask at <a className="underline" href={`mailto:${contact}`}>{contact}</a>.
                </>
              )}
            </p>
          )}
        </div>
      </section>

      <Sample />

      <section className="mt-10 grid gap-3 sm:grid-cols-2">
        <Feature
          title="Targets that move with your water"
          body="Free chlorine has no fixed target — it depends on your stabiliser level. Pool Nerd computes the floor and the band from the CYA you last measured, and says when that number is too old to trust."
        />
        <Feature
          title="Doses from published chemistry"
          body="Every amount comes from a deterministic calculator pinned to reference values, and says which strength of product it assumed. Nothing you pour into water is ever arithmetic done by a language model."
        />
        <Feature
          title="A history that stays honest"
          body="Readings, doses, rain, drains, backwashes, a solar cover going on, equipment coming and going. Charts show gaps as gaps. Retired equipment is kept, so what filter you had in 2023 stays answerable."
        />
        <Feature
          title="An assistant that knows the log"
          body="Ask what to do about a reading and Claude answers from your actual history — what went in yesterday, what the overnight test said — and sequences the steps. It explains and prioritises; it never sizes a dose."
        />
      </section>

      <section className="mt-10">
        <p className="unit">Learn the method</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight">
          The guide: how Pool Nerd manages a pool
        </h2>
        <p className="mt-3 max-w-xl text-(--color-ink-muted)">
          The app enforces a method, and the method is written down. Two pages, free to read
          with or without an account, say what the numbers mean, what the targets are and why,
          what to add and what never to, and exactly how to run each test with the kit in your
          hand.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <Link to="/guide" className="card block p-5 transition-colors hover:border-(--color-border-strong)">
            <h3 className="font-medium">How to manage a pool</h3>
            <p className="mt-2 text-sm text-(--color-ink-muted)">
              The loop of test, dose and verify. The six numbers and what each one is for. The
              free-chlorine chart that follows your stabiliser. The weekly routine, the products
              worth buying and the tablets worth avoiding, and the common problems — green water,
              chlorine that vanishes by morning, a pH that will not stay down — with the fix for
              each, SLAM and overnight test included.
            </p>
            <p className="mt-3 text-sm font-medium">Read the guide →</p>
          </Link>
          <Link to="/guide/testing" className="card block p-5 transition-colors hover:border-(--color-border-strong)">
            <h3 className="font-medium">Testing procedures</h3>
            <p className="mt-2 text-sm text-(--color-ink-muted)">
              Reading by reading, for the kit you have: the Taylor K-2006 in full with every drop
              count and sample size, the TF-100 that shares its chemistry, the K-2005's colour
              match, and an honest word about strips and store panels. Set your kit in Setup and
              the page opens on it.
            </p>
            <p className="mt-3 text-sm font-medium">Read the procedures →</p>
          </Link>
        </div>
      </section>

      <section className="mt-10 card p-5">
        <h2 className="text-sm font-semibold">Built for the Taylor K-2006</h2>
        <p className="mt-2 text-sm text-(--color-ink-muted)">
          The quick test is free chlorine, pH, water temperature and filter pressure — the four
          numbers you check most days. The full panel adds combined chlorine, alkalinity,
          calcium hardness and cyanuric acid. Strip and pool-store results are logged too, with
          their method recorded, so a chart never mixes a titration with a guess.
        </p>
        <p className="mt-3 text-sm">
          <Link to="/guide" className="underline">
            How Pool Nerd manages a pool
          </Link>
          {" · "}
          <Link to="/guide/testing" className="underline">
            Testing procedures, reading by reading
          </Link>
        </p>
      </section>
    </PublicShell>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="card p-5">
      <h2 className="font-medium">{title}</h2>
      <p className="mt-2 text-sm text-(--color-ink-muted)">{body}</p>
    </div>
  );
}

/**
 * One reading, as the dashboard would show it. Static on purpose — a demo
 * account would be a second thing to keep truthful — but the figures are a
 * real pool on a real July evening, and the verdict is what the app said.
 */
function Sample() {
  const figures = [
    { label: "FC", value: "6.5", unit: "ppm", status: "ok", tone: "--color-reagent-dpd" },
    { label: "pH", value: "7.6", unit: "", status: "ok", tone: "--color-reagent-phenol" },
    { label: "TA", value: "80", unit: "ppm", status: "ok", tone: "--color-reagent-bromocresol" },
    { label: "CYA", value: "50", unit: "ppm", status: "ok", tone: "--color-reagent-cyanuric" },
  ];
  return (
    <section aria-label="Example reading" className="card overflow-hidden">
      <div className="flex items-baseline justify-between px-5 pt-4">
        <p className="unit">Latest reading · Backyard pool</p>
        <p className="unit">Thu 6:40 pm</p>
      </div>
      <div className="grid grid-cols-4 gap-2 px-5 py-4">
        {figures.map((f) => (
          <div key={f.label}>
            <p className="unit">{f.label}</p>
            <p className="figure mt-1 text-2xl" style={{ color: `var(${f.tone})` }}>
              {f.value}
            </p>
            <p className="text-xs text-(--color-ink-faint)">{f.unit || " "}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-(--color-border-subtle) bg-(--color-surface-sunken) px-5 py-3 text-sm">
        <span className="font-medium">Nothing to add.</span>{" "}
        <span className="text-(--color-ink-muted)">
          FC is in the 6–8 band for CYA 50. About 30 hours of cover before the next test.
        </span>
      </div>
    </section>
  );
}
